import React from 'react';
import { Bell, Calendar, Video, Image as ImageIcon, MessageSquare, ExternalLink } from 'lucide-react';
import { markNotificationAsRead } from '../lib/notification';
import { useOpenLink } from './LinkHandler';

interface NotificationItemProps {
  notification: {
    id: string;
    title: string;
    message: string;
    type: 'text' | 'image' | 'video';
    media_url?: string;
    link_url?: string;
    created_at: string;
    is_read: boolean;
  };
  onMarkAsRead?: () => void;
}

export default function NotificationItem({ notification, onMarkAsRead }: NotificationItemProps) {
  const openLink = useOpenLink();
  
  const handleClick = async () => {
    // Marcar como lida
    if (!notification.is_read) {
      try {
        const success = await markNotificationAsRead(notification.id);
        if (success && onMarkAsRead) {
          onMarkAsRead();
        }
      } catch (error) {
        console.error('Erro ao marcar notificação como lida:', error);
      }
    }

    // Abrir link se existir
    if (notification.link_url) {
      await openLink(notification.link_url);
    }
  };

  function getNotificationIcon() {
    switch (notification.type) {
      case 'image':
        return <ImageIcon className="h-5 w-5" />;
      case 'video':
        return <Video className="h-5 w-5" />;
      default:
        return <MessageSquare className="h-5 w-5" />;
    }
  }

  return (
    <div 
      onClick={handleClick}
      className={`bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
        !notification.is_read ? 'border-l-4 border-brand-teal' : ''
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center">
          <div className={`p-2 rounded-full mr-3 ${
            notification.type === 'image' ? 'bg-blue-100 text-blue-600' :
            notification.type === 'video' ? 'bg-red-100 text-red-600' :
            'bg-purple-100 text-purple-600'
          }`}>
            {getNotificationIcon()}
          </div>
          <h3 className="font-semibold text-brand-teal">{notification.title}</h3>
        </div>
        <span className="text-sm text-gray-500">
          {new Date(notification.created_at).toLocaleDateString('pt-BR')}
        </span>
      </div>
      
      <p className="text-gray-600 ml-12">{notification.message}</p>
      
      {notification.media_url && (
        <div className="mt-4 ml-12">
          {notification.type === 'image' ? (
            <img 
              src={notification.media_url} 
              alt={notification.title}
              className="rounded-lg max-h-32 object-cover"
              onError={(e) => {
                // Fallback for broken images
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=Imagem+não+disponível';
              }}
            />
          ) : notification.type === 'video' ? (
            <div className="aspect-video w-full max-w-md bg-gray-100 rounded-lg overflow-hidden">
              <iframe
                src={notification.media_url}
                className="w-full h-full"
                allowFullScreen
                title={notification.title}
                sandbox="allow-scripts allow-same-origin allow-presentation"
                loading="lazy"
              />
            </div>
          ) : null}
        </div>
      )}
      
      {notification.link_url && (
        <div className="mt-4 ml-12">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openLink(notification.link_url!);
            }}
            className="text-brand-teal hover:underline inline-flex items-center"
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            Abrir link
          </button>
        </div>
      )}
      
      {!notification.is_read && (
        <div className="mt-4 ml-12">
          <span className="inline-block text-xs bg-brand-light text-brand-teal px-2 py-1 rounded-full">
            Não lida
          </span>
        </div>
      )}
    </div>
  );
}